import { getAnalytics } from "@tm/insurance/analytics";
import { replaceHistory } from "@tm/insurance/utils";
import { TRAVELLER_NUMBER_CONSTANTS } from "@tm/isomorphic-utils";
import { usePlanType } from "@tm/ui";
import { useBreakpointV2 } from "@tm/ui-widgets";
import React, { useEffect, useRef, useState } from "react";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import { ImageComponent } from "../ImageComponent/ImageComponent";
import LandingButton from "../LandingButton/LandingButton";
import SectionDividers from "../SectionDividers/SectionDividers";
import SpanTag from "../TextStyles/SpanTag";
import { ITravellerInfoSelectSection } from "../TravelDetails/types";
import {
  AgeRangeSectionWrap,
  AgeRangeSelect,
  AgeRangeSelectMobileWrap,
  AgeRangeSelectSection,
  AgeSelectItem,
  ApplyCtaSectionWrap,
  CounterBtn,
  CounterWrap,
  CountSectionWrap,
  RangePickerContentWrap,
  TravellerAgenCountHeading,
  TravellerAgeRangePickerWrap,
  TravellerAgeRangeSelectSection,
  UpsideDownTriangle,
} from "./TravellingToWidget.style";

export interface ITravCountnAgeRangePicker {
  travellerInfoSelectSection?: ITravellerInfoSelectSection;
  captureCount: number;
  travAgeRange: any[];
  focusedIndex: number;
  setIsAgeRangeSelectedError: React.Dispatch<React.SetStateAction<boolean>>;
  ageRangeSelectIndex: number;
  ageRangeSelectRef?: React.MutableRefObject<HTMLDivElement | null>;
  setAgeRangeSelectIndex: React.Dispatch<React.SetStateAction<number>>;
  setFocusedIndex: React.Dispatch<React.SetStateAction<number>>;
  setTravAgeRange: React.Dispatch<React.SetStateAction<any[]>>;
  setCaptureCount: React.Dispatch<React.SetStateAction<number>>;
  travellingToRef?: React.RefObject<HTMLDivElement>;
}

const TravAgeRangenCountPicker = ({
  travellerInfoSelectSection,
  captureCount,
  travAgeRange,
  setCaptureCount,
  setTravAgeRange,
  focusedIndex,
  ageRangeSelectIndex,
  ageRangeSelectRef,
  setIsAgeRangeSelectedError,
  setFocusedIndex,
  setAgeRangeSelectIndex,
  travellingToRef,
}: ITravCountnAgeRangePicker) => {
  const { countSection, ageRangeSection, ctaText } =
    travellerInfoSelectSection || {};
  const [selectPosition, setSelectPosition] = useState({
    left: 0,
    top: 0,
    bottom: 0,
  });
  const [travellerCount, setTravellerCount] = useState(1);
  const [travellerAgeRange, setTravellerAgeRange] = useState<any[]>([]);
  const [isSelectedError, setIsSelectedError] = useState(false);
  const divRefs = useRef<any>([]);
  const applyBtnRef = useRef<HTMLButtonElement | null>(null);
  const device = useBreakpointV2();
  const analytics = getAnalytics();
  const { type } = usePlanType();

  const handleSelectPosition = (index: number) => {
    const currentDiv = divRefs.current[index];
    if (currentDiv) {
      const rect = currentDiv.getBoundingClientRect();
      setSelectPosition({
        left: rect.x - (device === "sm" ? 8 : 0),
        top: rect.top - (device === "sm" ? 150 : 0),
        bottom: window.innerHeight - rect.top + 7,
      });
    }
  };

  useEffect(() => {
    const button = applyBtnRef.current;
    let viewportMargin = 16;

    if (button) {
      const rect = button.getBoundingClientRect();
      const offsetTop = window.scrollY + rect.top;

      const isInViewport =
        offsetTop >= 0 &&
        offsetTop + rect.height <= window.innerHeight - viewportMargin;

      if (!isInViewport) {
        travellingToRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      }
    }
  }, []);

  useEffect(() => {
    setTravellerCount(captureCount);
    setTravellerAgeRange(travAgeRange);
  }, [captureCount, travAgeRange, focusedIndex]);

  const handleTravellerCountIncr = () => {
    const newTravAgeRange = [...(travellerAgeRange ?? [])];
    if (
      travellerCount <
      (countSection?.maxCount ?? TRAVELLER_NUMBER_CONSTANTS.MAX_TRAVELLERS)
    ) {
      setTravellerCount(travellerCount + 1);
      newTravAgeRange.push({});
    } else {
      setTravellerCount(
        countSection?.maxCount ?? TRAVELLER_NUMBER_CONSTANTS.MAX_TRAVELLERS
      );
      if (newTravAgeRange.length < TRAVELLER_NUMBER_CONSTANTS.MAX_TRAVELLERS) {
        newTravAgeRange.push({});
      }
    }
    setTravellerAgeRange?.(newTravAgeRange);
  };
  const handleTravellerCountDecr = () => {
    const newTravAgeRange = [...(travellerAgeRange ?? [])];
    if (travellerCount > TRAVELLER_NUMBER_CONSTANTS.MIN_TRAVELLERS) {
      newTravAgeRange.pop();
      setTravellerCount(travellerCount - 1);
    } else {
      setTravellerCount(TRAVELLER_NUMBER_CONSTANTS.MIN_TRAVELLERS);
      if (newTravAgeRange.length > TRAVELLER_NUMBER_CONSTANTS.MIN_TRAVELLERS) {
        newTravAgeRange.pop();
      }
    }
    setTravellerAgeRange(newTravAgeRange);
  };

  const handleAgeRangeSelect = (
    index: number,
    value: [number, number],
    label: string,
    key: number,
    e: any
  ) => {
    setAgeRangeSelectIndex(-1);
    const newTravAgeRange = travellerAgeRange?.map((item, i) => {
      if (i === index) {
        return { value, label, key };
      }
      return item;
    });
    setTravellerAgeRange(newTravAgeRange ?? []);
    e.stopPropagation();
  };

  const handleApply = () => {
    const isAgeRangeSelected = travellerAgeRange?.every(
      (ageRange) => ageRange?.value
    );
    if (isAgeRangeSelected) {
      setIsAgeRangeSelectedError(false);
      setFocusedIndex(-1);
      setTravAgeRange(travellerAgeRange);
      setCaptureCount(travellerCount);
      analytics?.trackLandingEvents(
        {
          actionType: "Ins_TravellerAndAgeModified",
        },
        "pageView",
        type
      );
      replaceHistory("travellers", travellerCount.toString());
      replaceHistory(
        "travellerAgeRanges",
        JSON.stringify(travellerAgeRange.map((item) => item.key))
      );
    } else {
      setIsSelectedError(true);
    }
  };

  return (
    <TravellerAgeRangePickerWrap isBottomSheetFullScreen={travellerCount > 10}>
      {device === "sm" && (
        <TravellerAgenCountHeading>
          <div
            onClick={() => {
              setFocusedIndex(-1);
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z"
                fill="#808080"
              />
            </svg>
          </div>
          <SpanTag fontSize="md" fontWeight="bold" color="#4A4A4A">
            TRAVELLERS & AGE
          </SpanTag>
        </TravellerAgenCountHeading>
      )}
      <RangePickerContentWrap isBottomSheetFullScreen={travellerCount > 10}>
        <CountSectionWrap>
          <FlexComp justifyContent="space-between" alignItems="center">
            <SpanTag fontSize="sm">
              {countSection?.title && (
                <SpanTag fontSize="xs" fontWeight="bold" color="#4A4A4A">
                  <FormattedText value={countSection?.title} />
                </SpanTag>
              )}
            </SpanTag>
            <CounterWrap
              borderRadius="8px"
              padding="4px"
              gap="4px"
              height="32px"
              boxShadow="0px"
              border="1px solid #D8D8D8"
            >
              <CounterBtn
                isDiabled={travellerCount == 1}
                onClick={handleTravellerCountDecr}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="24" height="24" fill="white" />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M6 11.0938H17.5V12.9064H6V11.0938Z"
                    fill="#008CFF"
                  />
                </svg>
              </CounterBtn>
              <SpanTag fontWeight="bold" fontSize="large">
                {travellerCount && travellerCount <= 9
                  ? "0" + travellerCount
                  : travellerCount}
              </SpanTag>
              <CounterBtn
                isDiabled={travellerCount == countSection?.maxCount}
                onClick={handleTravellerCountIncr}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <rect width="24" height="24" fill="white" />
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M12.612 6.04785V11.1719H17.496V12.7919H12.612V17.9519H10.86V12.7919H6V11.1719H10.86V6.04785H12.612Z"
                    fill="#008CFF"
                  />
                </svg>
              </CounterBtn>
            </CounterWrap>
          </FlexComp>
          <SpanTag fontSize="sm">
            {ageRangeSection?.title && (
              <FormattedText value={countSection?.description} />
            )}
          </SpanTag>
        </CountSectionWrap>
        <div style={{ position: "relative", zIndex: "-1" }}>
          <SectionDividers xs />
        </div>
        <AgeRangeSectionWrap isBottomSheetFullScreen={travellerCount > 10}>
          <FlexComp flexDirection="column" gap="4px">
            {ageRangeSection?.title && (
              <SpanTag fontSize="xs" fontWeight="bold" color="#4A4A4A">
                <FormattedText value={ageRangeSection?.title} />
              </SpanTag>
            )}
            {ageRangeSection?.description && (
              <SpanTag fontSize="xs" color="#757575">
                <FormattedText value={ageRangeSection?.description} />
              </SpanTag>
            )}
          </FlexComp>
        </AgeRangeSectionWrap>
        <TravellerAgeRangeSelectSection>
          {travellerAgeRange?.map((ageRange, index) => {
            return (
              <FlexComp
                alignItems={device === "sm" ? "flex-end" : "center"}
                flexDirection="column"
              >
                <FlexComp
                  width="100%"
                  gap="4px"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <SpanTag fontSize="xs" margin="8px 0px">
                    Traveller {(index + 1).toString()}
                  </SpanTag>
                  <div>
                    {device === "lg" &&
                      (ageRangeSelectIndex === index ? (
                        <AgeRangeSelect
                          ref={ageRangeSelectRef}
                          top={selectPosition?.top}
                          left={selectPosition?.left}
                        >
                          {ageRangeSection?.ageRanges &&
                            ageRangeSection?.ageRanges?.map((age, i) => {
                              return (
                                <AgeSelectItem
                                  onClick={(e) => {
                                    handleAgeRangeSelect(
                                      index,
                                      [age?.min, age?.max],
                                      age?.label,
                                      i,
                                      e
                                    );
                                  }}
                                >
                                  <FormattedText
                                    value={age?.label}
                                    baseStyle={{ fontSize: "12px" }}
                                  />
                                </AgeSelectItem>
                              );
                            })}
                        </AgeRangeSelect>
                      ) : (
                        <AgeRangeSelectSection
                          ref={(el) => (divRefs.current[index] = el)}
                          onClick={(e) => {
                            setAgeRangeSelectIndex(index);
                            handleSelectPosition(index);
                            e.stopPropagation();
                          }}
                        >
                          <FlexComp justifyContent="space-between" width="100%">
                            {ageRange?.value ? (
                              <FormattedText
                                value={ageRange?.label}
                                baseStyle={{
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                }}
                              />
                            ) : (
                              <SpanTag fontSize="xs" color="#4A4A4A">
                                Select Age
                              </SpanTag>
                            )}
                            <FlexComp
                              alignItems="center"
                              height="16px"
                              width="16px"
                              isCursorPointer={true}
                              justifyContent="center"
                            >
                              <ImageComponent
                                height="5px"
                                width="8px"
                                src="https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                              />
                            </FlexComp>
                          </FlexComp>
                        </AgeRangeSelectSection>
                      ))}

                    {device === "sm" && (
                      <AgeRangeSelectSection>
                        {ageRangeSelectIndex === index && (
                          <AgeRangeSelectMobileWrap
                            top={selectPosition?.top}
                            left={selectPosition?.left}
                            bottom={selectPosition?.bottom}
                          >
                            <AgeRangeSelect
                              ref={ageRangeSelectRef}
                              top={selectPosition?.top}
                              left={selectPosition?.left}
                            >
                              {ageRangeSection?.ageRanges &&
                                ageRangeSection?.ageRanges?.map((age, i) => {
                                  return (
                                    <AgeSelectItem
                                      onClick={(e) => {
                                        handleAgeRangeSelect(
                                          index,
                                          [age?.min, age?.max],
                                          age?.label,
                                          i,
                                          e
                                        );
                                      }}
                                    >
                                      <FormattedText
                                        value={age?.label}
                                        baseStyle={{
                                          fontSize: "12px",
                                        }}
                                      />
                                    </AgeSelectItem>
                                  );
                                })}
                            </AgeRangeSelect>
                            <UpsideDownTriangle />
                          </AgeRangeSelectMobileWrap>
                        )}
                        <div
                          ref={(el) => (divRefs.current[index] = el)}
                          onClick={(e) => {
                            setAgeRangeSelectIndex(index);
                            handleSelectPosition(index);
                            e.stopPropagation();
                          }}
                        >
                          <FlexComp justifyContent="space-between" width="100%">
                            {ageRange?.value ? (
                              <FormattedText
                                value={ageRange?.label}
                                baseStyle={{
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                }}
                              />
                            ) : (
                              <SpanTag fontSize="xs" color="#4A4A4A">
                                Select Age
                              </SpanTag>
                            )}
                            <FlexComp
                              alignItems="center"
                              height="16px"
                              width="16px"
                              isCursorPointer={true}
                              justifyContent="center"
                            >
                              <ImageComponent
                                height="5px"
                                width="8px"
                                src="https://tripmoneycmsimgak.mmtcdn.com/img/Arrow_Down_Blue_5d58cf6783.svg"
                              />
                            </FlexComp>
                          </FlexComp>
                        </div>
                      </AgeRangeSelectSection>
                    )}
                  </div>
                </FlexComp>
                {isSelectedError && !ageRange?.value && (
                  <SpanTag
                    fontSize="xs"
                    color="#EB2026"
                    margin={
                      device === "lg" ? "0px 0px 0px 60px" : "0px 3px 0px 0px"
                    }
                  >
                    Select age range
                  </SpanTag>
                )}
              </FlexComp>
            );
          })}
        </TravellerAgeRangeSelectSection>
      </RangePickerContentWrap>
      <ApplyCtaSectionWrap>
        <LandingButton
          customWidth={device === "sm" ? "100%" : "93px"}
          customHeight={device === "sm" ? "44px" : "32px"}
          customTitle={ctaText}
          fontSize={device === "sm" ? "16px" : "14px"}
          onClick={handleApply}
          buttonref={applyBtnRef}
        />
      </ApplyCtaSectionWrap>
    </TravellerAgeRangePickerWrap>
  );
};

export default TravAgeRangenCountPicker;
